
import React from 'react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Course, EnrolledCourse } from '@/types';
import { GraduationCap, Clock, Star, PlayCircle, Award } from 'lucide-react';

interface CourseCardProps {
  course: Course;
  enrolledCourse?: EnrolledCourse;
  onEnroll?: (courseId: string) => void;
  onContinue?: (courseId: string, moduleId: string) => void;
}

const CourseCard: React.FC<CourseCardProps> = ({ 
  course, 
  enrolledCourse,
  onEnroll,
  onContinue
}) => {
  const isEnrolled = !!enrolledCourse;
  const isCompleted = isEnrolled && enrolledCourse.progress === 100;
  
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins}m`;
    } else if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${mins}m`;
    }
  };
  
  const getLevelBadge = (level: string) => {
    switch (level) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-blue-100 text-blue-800';
      case 'advanced':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  return (
    <Card className="h-full flex flex-col overflow-hidden transition-all hover:shadow-md">
      <div className="relative aspect-video w-full overflow-hidden">
        <img 
          src={course.thumbnailUrl} 
          alt={course.title} 
          className="object-cover w-full h-full transition-transform hover:scale-105"
        />
        {isEnrolled && !isCompleted && (
          <div className="absolute bottom-0 left-0 right-0 bg-background/90 p-2">
            <Progress value={enrolledCourse.progress} className="h-1.5" />
            <p className="text-xs text-center mt-1">{enrolledCourse.progress}% complete</p>
          </div>
        )}
        {isCompleted && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Badge variant="soundcharts-filled" className="text-lg px-4 py-1">Completed</Badge>
          </div>
        )}
      </div>
      
      <CardHeader className="pb-2 pt-4">
        <div className="flex flex-wrap gap-2 mb-2">
          <Badge variant="outline" className={getLevelBadge(course.level)}>
            {course.level.charAt(0).toUpperCase() + course.level.slice(1)}
          </Badge>
          
          <Badge variant="outline" className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{formatDuration(course.duration)}</span>
          </Badge>
          
          <Badge variant="outline" className="flex items-center gap-1">
            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
            <span>{course.rating.toFixed(1)} ({course.ratingCount})</span>
          </Badge>
        </div>
        
        <h3 className="font-semibold text-lg line-clamp-2">{course.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2">{course.description}</p>
      </CardHeader>
      
      <CardContent className="pb-2 flex-grow">
        <div className="flex flex-wrap gap-1 mb-3">
          {course.tags.slice(0, 3).map((tag, index) => (
            <Badge key={index} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
          {course.tags.length > 3 && (
            <Badge variant="secondary" className="text-xs">
              +{course.tags.length - 3} more
            </Badge>
          )}
        </div>
        
        <div className="flex items-center text-sm text-muted-foreground">
          <GraduationCap className="h-4 w-4 mr-2" />
          <span>Instructor: {course.instructor}</span>
        </div>
        
        {course.xpReward > 0 && (
          <div className="mt-3 flex items-center gap-2">
            <Badge variant="outline" className="flex items-center gap-1">
              <Award className="h-3 w-3" />
              <span>{course.xpReward} XP Reward</span>
            </Badge>
          </div>
        )}
      </CardContent>
      
      <CardFooter className="pt-2">
        {isEnrolled ? (
          <Button 
            className="w-full gap-2" 
            onClick={() => onContinue && onContinue(course.id, enrolledCourse.currentModuleId)}
          >
            {isCompleted ? (
              <>
                <GraduationCap className="h-4 w-4" />
                <span>Review Course</span>
              </>
            ) : (
              <>
                <PlayCircle className="h-4 w-4" />
                <span>Continue Learning</span>
              </>
            )}
          </Button>
        ) : (
          <Button 
            className="w-full" 
            variant="outline"
            onClick={() => onEnroll && onEnroll(course.id)}
          >
            Enroll Now
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default CourseCard;
